<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import LiaAgentsAPI from 'dashboard/api/liaAgents';
import LiaIntegrationsAPI from 'dashboard/api/liaIntegrations';
import StepSelectVersion from '../components/wizard/StepSelectVersion.vue';
import StepConfigureAgent from '../components/wizard/StepConfigureAgent.vue';
import StepReviewActivate from '../components/wizard/StepReviewActivate.vue';

const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

// State
const currentStep = ref(1);
const isLoading = ref(true);
const isCreating = ref(false);

// Selected version
const selectedVersion = ref(null);

// Agent configuration
const agentConfig = ref({
  name: '',
  company_name: '',
  inbox_ids: [],
  erp_type: 'ixc',
  erp_url: '',
  erp_token: '',
  isValid: false,
});

// Computed
const entitlements = computed(
  () => store.getters['liaAgents/getEntitlements'] || {}
);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

// Step labels
const steps = [
  { id: 1, label: 'Versao', icon: 'i-lucide-layers' },
  { id: 2, label: 'Configurar', icon: 'i-lucide-settings' },
  { id: 3, label: 'Ativar', icon: 'i-lucide-rocket' },
];

// Can proceed to next step?
const canProceed = computed(() => {
  if (currentStep.value === 1) {
    return selectedVersion.value !== null;
  }
  if (currentStep.value === 2) {
    return agentConfig.value.isValid;
  }
  return true;
});

// Navigation
const nextStep = () => {
  if (canProceed.value && currentStep.value < 3) {
    currentStep.value++;
  }
};

const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const goToList = () => {
  router.push(accountScopedRoute('lia_agents'));
};

// Select version
const handleVersionSelect = version => {
  selectedVersion.value = version;
};

// Create agent
const createAgent = async () => {
  isCreating.value = true;

  try {
    // 1. Create integration if LIA 3.1
    let integrationId = null;
    if (selectedVersion.value === '3.1') {
      const integrationResponse = await LiaIntegrationsAPI.create({
        name: `ERP ${agentConfig.value.company_name}`,
        provider: agentConfig.value.erp_type,
        credentials: {
          api_url: agentConfig.value.erp_url,
          api_token: agentConfig.value.erp_token,
        },
        active: true,
      });
      integrationId = integrationResponse.data.id;
    }

    // 2. Create agent
    const agentData = {
      name: agentConfig.value.name,
      company_name: agentConfig.value.company_name,
      version: selectedVersion.value,
      inbox_ids: agentConfig.value.inbox_ids,
      lia_integration_id: integrationId,
    };

    const agentResponse = await LiaAgentsAPI.create(agentData);
    const agentId = agentResponse.data.id;

    // 3. Activate agent (deploys workflow)
    await LiaAgentsAPI.activate(agentId);

    useAlert('Agente criado e ativado com sucesso!');

    // 4. Redirect to edit page
    router.push(accountScopedRoute('lia_agents_edit', { agentId }));
  } catch (error) {
    console.error('Error creating agent:', error);
    useAlert(error.response?.data?.error || 'Erro ao criar agente');
  } finally {
    isCreating.value = false;
  }
};

// Load data
onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch('liaAgents/fetchEntitlements'),
      store.dispatch('inboxes/get'),
    ]);
  } catch (error) {
    console.error('Error loading data:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="min-h-screen bg-n-background">
    <!-- Header -->
    <div class="bg-n-solid-2 border-b border-n-weak">
      <div class="max-w-4xl mx-auto px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <button
              type="button"
              class="p-2 hover:bg-n-alpha-3 rounded-lg transition-colors"
              @click="goToList"
            >
              <span class="i-lucide-arrow-left size-5 text-n-slate-11" />
            </button>
            <div>
              <h1 class="text-xl font-bold text-n-slate-12">Novo Agente LIA</h1>
              <p class="text-sm text-n-slate-11">
                Crie um novo agente de atendimento inteligente
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Steps indicator -->
    <div class="bg-n-solid-2 border-b border-n-weak">
      <div class="max-w-4xl mx-auto px-6 py-4">
        <div class="flex items-center justify-center gap-4">
          <template v-for="(step, index) in steps" :key="step.id">
            <!-- Step -->
            <div
              class="flex items-center gap-2"
              :class="[
                currentStep >= step.id ? 'text-woot-500' : 'text-n-slate-9',
              ]"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors"
                :class="[
                  currentStep >= step.id
                    ? 'bg-woot-500 text-white'
                    : 'bg-n-alpha-3 text-n-slate-11',
                ]"
              >
                <span
                  v-if="currentStep > step.id"
                  class="i-lucide-check size-4"
                />
                <span v-else>{{ step.id }}</span>
              </div>
              <span class="text-sm font-medium hidden sm:inline">
                {{ step.label }}
              </span>
            </div>

            <!-- Connector -->
            <div
              v-if="index < steps.length - 1"
              class="w-12 h-0.5"
              :class="[currentStep > step.id ? 'bg-woot-500' : 'bg-n-alpha-3']"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto px-6 py-8">
      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <span class="i-lucide-loader-2 size-8 animate-spin text-woot-500" />
      </div>

      <!-- Steps -->
      <template v-else>
        <!-- Step 1: Select Version -->
        <StepSelectVersion
          v-if="currentStep === 1"
          :selected-version="selectedVersion"
          :entitlements="entitlements"
          @select="handleVersionSelect"
        />

        <!-- Step 2: Configure -->
        <StepConfigureAgent
          v-if="currentStep === 2"
          v-model="agentConfig"
          :version="selectedVersion"
        />

        <!-- Step 3: Review & Activate -->
        <StepReviewActivate
          v-if="currentStep === 3"
          :version="selectedVersion"
          :config="agentConfig"
          :inboxes="inboxes"
          :is-creating="isCreating"
          @create="createAgent"
        />
      </template>
    </div>

    <!-- Footer Navigation -->
    <div
      v-if="!isLoading"
      class="fixed bottom-0 left-0 right-0 bg-n-solid-2 border-t border-n-weak"
    >
      <div class="max-w-4xl mx-auto px-6 py-4">
        <div class="flex items-center justify-between">
          <button
            v-if="currentStep > 1"
            type="button"
            class="flex items-center gap-2 px-4 py-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
            @click="prevStep"
          >
            <span class="i-lucide-arrow-left size-4" />
            Voltar
          </button>
          <div v-else />

          <button
            v-if="currentStep < 3"
            type="button"
            class="flex items-center gap-2 px-6 py-2 bg-woot-500 hover:bg-woot-600 text-white rounded-lg transition-colors disabled:opacity-50"
            :disabled="!canProceed"
            @click="nextStep"
          >
            Continuar
            <span class="i-lucide-arrow-right size-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
